// Sugar Alarm — the kiosk camera-purpose bridge, page side (cycle 455).
//
// The Android kiosk APK (vc41+) registers `window.sugalarmNative` with
// WebViewCompat.addWebMessageListener for exactly https://sugalarm.fly.dev,
// main frame only. The page may say exactly three things to it, as exactly
// three strings — the same three the APK matches byte for byte
// (KioskCameraBridge.Message, pinned by golden vectors on both sides):
//
//   * "this file input wants the meal camera" (the rear lens, directly);
//   * "this file input wants the avatar camera" (cycle 328's front square);
//   * "an alarm has started" (close any native camera or photo dialog).
//
// ⚠ A FIXED ENUM, ONE-SHOT, AND SILENT WHEN ABSENT. No URL, path, value or
// free text ever rides this channel. A purpose is sent immediately before the
// input is clicked (`openPhotoInput` is the one place that does both, in that
// order); the APK consumes it on the next chooser only. On an older APK, a
// phone, a desktop browser or any page without the object, every call here is
// a silent no-op — no error, no console line — and the input opens exactly as
// it did before cycle 455.
//
// This module is also the one owner of "is an alarm sounding, as far as the
// photo buttons are concerned" (`photoBlockedByAlarm`): App.tsx publishes it,
// the kiosk photo entry points read it and stay disabled while it is true.

import { useSyncExternalStore } from "react";
import { isNativeKioskShell } from "./capacitor";

export type CameraPurpose = "meal_photo" | "avatar";

/** The exact message bodies. Do not build these at a call site. */
export const CAMERA_MESSAGES = {
  meal_photo: '{"type":"camera_purpose","purpose":"meal_photo"}',
  avatar: '{"type":"camera_purpose","purpose":"avatar"}',
  alarm_started: '{"type":"alarm_started"}',
} as const;

interface NativePort {
  postMessage(message: string): void;
}

function nativePort(): NativePort | null {
  if (typeof window === "undefined") return null;
  // Cycle 458 (astra): a bridge probe that throws (a half-loaded Capacitor)
  // reads as "no bridge" — it must never stop a photo input opening.
  try {
    if (!isNativeKioskShell()) return null;
  } catch {
    return null;
  }
  const port = (window as unknown as { sugalarmNative?: unknown }).sugalarmNative;
  if (
    port == null ||
    typeof (port as { postMessage?: unknown }).postMessage !== "function"
  ) {
    return null;
  }
  return port as NativePort;
}

/** Is the camera bridge present? False on an old APK and off the kiosk. */
export function cameraBridgeAvailable(): boolean {
  return nativePort() != null;
}

/** What happened to one post: delivered to the bridge, or why not. */
export type PostOutcome = "sent" | "no_bridge" | "post_failed";

function postWithOutcome(body: string): PostOutcome {
  const port = nativePort();
  if (!port) return "no_bridge";
  try {
    port.postMessage(body);
    return "sent";
  } catch {
    return "post_failed";
  }
}

function post(body: string): boolean {
  return postWithOutcome(body) === "sent";
}

/** Tell the APK which camera the NEXT file chooser wants. */
export function sendCameraPurpose(purpose: CameraPurpose): boolean {
  return post(CAMERA_MESSAGES[purpose]);
}

// ─── Cycle 458: the purpose that never reached the APK ────────────────────
//
// On the kiosk APK every photo input now carries `capture`, so a chooser with
// no purpose routes to the in-app FRONT camera (never a gallery). The page can
// see exactly one cause of that: its purpose never reached the APK — no bridge
// on this build (an APK older than vc41, or a WebView without
// WEB_MESSAGE_LISTENER), or the post threw. Those are reported to the server
// (`/api/health.photos.kiosk_camera`). A purpose that WAS posted but missed
// the APK's 400 ms pairing window is invisible here — the APK will report it
// (`timing_miss`) in its own cycle. Reporting is telemetry only: fire and
// forget, at most one report per purpose per minute, never awaited, never
// able to stop the camera opening. Off the kiosk APK nothing is reported.

const REPORT_MIN_INTERVAL_MS = 60_000;
const lastReportAt: Record<CameraPurpose, number> = {
  meal_photo: Number.NEGATIVE_INFINITY,
  avatar: Number.NEGATIVE_INFINITY,
};

type FallbackReporter = (purpose: CameraPurpose, reason: "no_bridge" | "post_failed") => void;

const defaultReporter: FallbackReporter = (purpose, reason) => {
  void import("../api/devices")
    .then((m) => m.reportCameraFallback({ purpose, reason }))
    .catch(() => undefined);
};

let reporter: FallbackReporter = defaultReporter;

/** Tests only. */
export function setCameraFallbackReporterForTests(r: FallbackReporter | null): void {
  reporter = r ?? defaultReporter;
  lastReportAt.meal_photo = Number.NEGATIVE_INFINITY;
  lastReportAt.avatar = Number.NEGATIVE_INFINITY;
}

/** Send the purpose; on the kiosk APK, report it if it never reached the APK.
 *  Returns what happened to the post. */
export function sendCameraPurposeReporting(
  purpose: CameraPurpose,
  nowMs: number = Date.now(),
): PostOutcome {
  const outcome = postWithOutcome(CAMERA_MESSAGES[purpose]);
  if (outcome === "sent") return outcome;
  let kioskApk = false;
  try {
    kioskApk = isNativeKioskShell();
  } catch {
    kioskApk = false;
  }
  if (!kioskApk) return outcome;
  if (nowMs - lastReportAt[purpose] < REPORT_MIN_INTERVAL_MS) return outcome;
  lastReportAt[purpose] = nowMs;
  try {
    reporter(purpose, outcome);
  } catch {
    /* telemetry never throws into a tap */
  }
  return outcome;
}

/** Tell the APK an alarm has started: it closes any native photo surface. */
export function sendAlarmStarted(): boolean {
  return post(CAMERA_MESSAGES.alarm_started);
}

/** Send the purpose, THEN click the input — in that order, in the same tap.
 *  Every kiosk photo button opens its input through this, and it refuses on
 *  its own while an alarm sounds on the kiosk (the buttons are also shown
 *  disabled; this covers any render the gate has not reached yet). */
export function openPhotoInput(
  input: HTMLInputElement | null | undefined,
  purpose: CameraPurpose,
): void {
  if (!input) return;
  if (photoBlockedByAlarm()) return;
  sendCameraPurposeReporting(purpose);
  input.click();
}

// ─── Alarm gating ─────────────────────────────────────────────────────────

/** What the photo gate is allowed to look at. */
export interface PhotoAlarmInput {
  /** The kiosk variant: the gate applies there only. */
  kiosk: boolean;
  /** `episode.audioOn` — a live episode that is not snoozed, stopped, held
   *  by a treating/quiet window or stood down. True for a high too; a
   *  STOPPED high is silenced, so it reads false and photos work again. */
  episodeAudioOn: boolean;
  /** `alarms.audibleAlarmLevel != null` — the low-side audio the bridge drives. */
  lowSideAudible: boolean;
  /** `alarms.noDataAlarmActive` — the dark-feed alarm (false once Silenced). */
  noDataAlarm: boolean;
}

/** "An alarm is sounding" for the photo buttons and for alarm_started. */
export function alarmSoundingForPhotos(i: PhotoAlarmInput): boolean {
  return i.kiosk && (i.episodeAudioOn || i.lowSideAudible || i.noDataAlarm);
}

/** The rising edge that sends alarm_started. */
export function alarmStartedForCamera(was: boolean, now: boolean): boolean {
  return now && !was;
}

let photoBlocked = false;
const listeners = new Set<() => void>();

/** App.tsx publishes the gate here. */
export function setPhotoBlockedByAlarm(blocked: boolean): void {
  if (photoBlocked === blocked) return;
  photoBlocked = blocked;
  listeners.forEach((l) => l());
}

export function photoBlockedByAlarm(): boolean {
  return photoBlocked;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** True while an alarm is sounding on the kiosk: photo buttons disable. */
export function usePhotoBlockedByAlarm(): boolean {
  return useSyncExternalStore(subscribe, photoBlockedByAlarm, () => false);
}

/** The line a disabled photo button shows (cycle 452's copy). */
export const PHOTO_BLOCKED_LINE = "Photo logging is available once the alarm is quiet.";
